/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import React from 'react';
import { inject, observer } from 'mobx-react';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import Framework, { shapes, LoadingHandler, StateErrorDisplay } from '@greenville/framework';
import Button from '@material-ui/core/Button';
import { toJS, observable, when } from 'mobx';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Typography, Grid, TextField } from '@material-ui/core';
import FormGroup from '@material-ui/core/FormGroup';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import Switch from '@material-ui/core/Switch';
// import Radio from '@material-ui/core/Radio';
// import RadioGroup from '@material-ui/core/RadioGroup';
// import Slider from '@material-ui/core/Slider';
import Books from '../../models/Books';
// import Input from '@material-ui/core/Input';
// import MenuBookIcon from '@material-ui/icons/MenuBook';
import Breadcrumbs from '../commonitems/Breadcrumbs';
import InterfaceModel from '../../models/InterfaceModel';
import PxeProductizationModel from '../../models/PxeProductizationModel';
import Loader from '../../../../common/Loader';
import NotificationModel from '../../../../common/components/notification/NotificationModel';
import UsersPermissions from '../../../users/models/UsersPermissions';
import TooltipCustom from '../../../../common/components/TooltipCustom';
import * as constants from '../../../../common/constants';

const LANGUAGE_INFO_YN_CHECKBOX_NAMES = [
  'isUnderLineHotspot',
  'audioPlayerOnereader',
  'studyToolOnereader',
  'serverSideEnabledOneReader',
  'print'
];

const styles = (theme) => ({
  root: {
    width: '99%',
    padding: theme.spacing(0, 1)
  },
  customWidth: {
    width: '95%'
  },
  gridMargin: {
    margin: theme.spacing(2, 0, 2, 2)
  },
  subgridMargin: {
    margin: theme.spacing(0, 0, 0, 4),
    flexFlow: 'initial'
  },
  textfieldInput: {
    fontSize: 13
  },
  pointer: {
    cursor: 'pointer'
  },
  formControl: {
    minWidth: 180,
    maxWidth: 300
  },
  colorPalette: {
    display: 'none'
  },
  swatchText: {
    display: 'inline-flex',
    alignItems: 'center'
  },
  swatchNum: {
    marginRight: '12px'
  },
  labelName: {
    alignItems: 'center',
    paddingTop: 8
  },
  switchLabelTypography: {
    fontSize: '0.875em',
    fontWeight: 600,
    lineHeight: 1.58,
    fontFamily: theme.typography.h6?.fontFamily || 'Open Sans, Calibri, Tahoma, sans-serif',
    color: theme.palette.text.primary
  },
  segmentControl: {
    display: 'inline-flex',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden'
  },
  segmentButton: {
    padding: theme.spacing(1, 2),
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[200],
    cursor: 'pointer',
    '&.active': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText
    }
  },
  offlineWarning: {
    color: theme.palette.error.main,
    fontSize: '0.875rem',
    marginTop: theme.spacing(1)
  },
  nextSectionBreak: {
    flexBasis: '100%',
    width: '100%'
  },
  pdzProductTextField: {
    '& .MuiOutlinedInput-root.Mui-disabled': {
      backgroundColor: theme.palette.grey[200]
    },
    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-input': {
      color: theme.palette.text.secondary,
      WebkitTextFillColor: theme.palette.text.secondary
    },
    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.grey[300]
    }
  }
});

@inject(
  'colorInterface',
  'colorLanguageStatus',
  'notificationState',
  'colorLanguagePostStatus',
  'booksState',
  'userPermissions',
  'pxeProductizationState'
)
@observer
class InterfaceColors extends React.Component {
  static propTypes = {
    match: PropTypes.object.isRequired,
    colorInterface: shapes.modelOf(InterfaceModel).isRequired,
    colorLanguageStatus: shapes.state.isRequired,
    classes: PropTypes.object.isRequired,
    colorLanguagePostStatus: shapes.state.isRequired,
    language: shapes.language.isRequired,
    notificationState: shapes.modelOf(NotificationModel).isRequired,
    booksState: shapes.modelOf(Books).isRequired,
    userPermissions: shapes.modelOf(UsersPermissions).isRequired,
    pxeProductizationState: shapes.modelOf(PxeProductizationModel).isRequired
  };

  PXE_FIELD_NAMES = [
    'gotoPage',
    'enablePaging',
    'offlineAccess',
    'enableIpadSwipe',
    'includeMathMLLib',
    'preventCopy',
    'enablePrinting'
  ];

  constructor(props) {
    super(props);
    const { colorInterface, language } = this.props;
    this.state = {
      colorInterface,
      colorInterfaceLabelName: [
        {
          type: <FormattedMessage {...language.getText('interface.HOTSPOT_COLOR')} />,
          themeID: 8,
          themeValue: '',
          uniqueID: ''
        },
        {
          type: <FormattedMessage {...language.getText('interface.UNDERLINE_HOTSPOT_COLOR')} />,
          themeID: 16,
          themeValue: '',
          uniqueID: ''
        },
        {
          type: <FormattedMessage {...language.getText('interface.HOTSPOT_HOVER')} />,
          themeID: 17,
          themeValue: '',
          uniqueID: ''
        },
        {
          type: <FormattedMessage {...language.getText('interface.HOTSPOT_THICKNESS')} />,
          themeID: 18,
          themeValue: '',
          uniqueID: ''
        },
        {
          type: <FormattedMessage {...language.getText('interface.REGIONAL_HOTSPOT_ALPHA')} />,
          themeID: 14,
          themeValue: '',
          uniqueID: ''
        },
        {
          type: <FormattedMessage {...language.getText('interface.UNDERLINE_HOTSPOT')} />,
          themeID: 20,
          themeValue: '',
          uniqueID: ''
        }
      ],
      inputSlideValue: 10,
      inputValue: 0,
      sliderValue: false,
      isSavePending: false,
      pdzValidationError: ''
    };
    this.pxeAudioBookDefaultApplied = false;
  }

  componentDidMount() {
    const { match, colorInterface, booksState, pxeProductizationState } = this.props;
    pxeProductizationState.reset();
    const { bookDetail } = booksState;
    colorInterface.fetch(match.params.bookid);
    const changedInput = Math.round((bookDetail.pageCount * 10) / 100);
    this.setState({
      inputValue: changedInput
    });
  }

  handlePxeSegmentChange = (value) => {
    const { colorInterface } = this.state;
    const { languageInfo } = colorInterface;
    const currentPxe = toJS(colorInterface.pageNavigationPrintSetting || {});
    this.setState({
      colorInterface: {
        ...colorInterface,
        languageInfo: { ...languageInfo, audioBook: value },
        pageNavigationPrintSetting: { ...currentPxe, audioBook: value }
      }
    });
  };

  handleChange = (e, textField) => {
    const { colorInterface } = this.state;
    const { languageInfo } = colorInterface;

    let updatedObject = {};

    if (textField) {
      updatedObject = {
        [e.target.name]: e.target.value
      };
    } else if (LANGUAGE_INFO_YN_CHECKBOX_NAMES.includes(e.target.name)) {
      updatedObject = {
        [e.target.name]: e.target.checked ? constants.YES : constants.NO
      };
    } else {
      updatedObject = {
        [e.target.name]: e.target.checked
      };
    }

    if (this.PXE_FIELD_NAMES.includes(e.target.name)) {
      const currentPxe = toJS(colorInterface.pageNavigationPrintSetting || {});
      this.setState({
        colorInterface: {
          ...colorInterface,
          pageNavigationPrintSetting: {
            ...currentPxe,
            [e.target.name]: e.target.checked
          }
        }
      });
    } else if (e.target.name === constants.PXE_NAV_MENU_LABEL_STUDY_TOOLS) {
      const { booksState } = this.props;
      const isEtext2PxeTitle = booksState?.bookDetail?.titleType === constants.ETEXT2_PXE_TITLE;
      const currentPxe = toJS(colorInterface.pageNavigationPrintSetting || {});
      const prevNavMenus = Array.isArray(currentPxe.navMenus) ? [...currentPxe.navMenus] : [];
      const studyIdx = prevNavMenus.findIndex((item) => item.label === constants.PXE_NAV_MENU_LABEL_STUDY_TOOLS);
      const isVisible = isEtext2PxeTitle ? false : e.target.checked;
      const nextNavMenus =
        studyIdx >= 0
          ? prevNavMenus.map((item, i) => (i === studyIdx ? { ...item, isVisible } : item))
          : [...prevNavMenus, { label: constants.PXE_NAV_MENU_LABEL_STUDY_TOOLS, isVisible }];
      this.setState({
        colorInterface: {
          ...colorInterface,
          pageNavigationPrintSetting: {
            ...currentPxe,
            navMenus: nextNavMenus
          }
        }
      });
    } else {
      this.setState({
        colorInterface: {
          ...colorInterface,
          languageInfo: { ...languageInfo, ...updatedObject }
        },
        ...(e.target.name === 'pdzProductID' ? { pdzValidationError: '' } : {})
      });
    }
  };

  handleSliderChange = (e, number, data) => {
    const slideValue = Math.round((data * number) / 100);
    this.setState({
      inputValue: slideValue,
      inputSlideValue: number
    });
  };

  handleInputChange = (e, data) => {
    const changedValue = e.target.value === 'NaN' || e.target.value === '' ? 0 : parseInt(e.target.value, 10);
    const inputMathValue = Math.round((changedValue / data) * 100);

    this.setState({
      inputValue: e.target.value,
      inputSlideValue: inputMathValue
    });
  };

  setFilterValues = (filterConfig) => filterConfig.map((filter) => <option value={filter.id}>{filter.value}</option>);

  swatchChange = (e, index, item) => {
    const { colorInterface } = this.state;
    const { colorInfo } = colorInterface;
    const { bookThemes } = colorInfo;
    const tempBookThemesValue = toJS(bookThemes);
    const matchedTheme = tempBookThemesValue.filter((filterItem) => filterItem.themeID === item.themeID);

    if (matchedTheme.length > 0) {
      if (item.themeID !== 14 && item.themeID !== 18) {
        matchedTheme[0].themeValue = e.target.value === '' ? '#000000' : e.target.value;
      } else {
        matchedTheme[0].themeValue = e.target.value === '' ? '0' : e.target.value;
      }
    } else {
      let newTheme;
      if (item.themeID !== 14 && item.themeID !== 18) {
        newTheme = {
          themeValue: e.target.value === '' ? '#000000' : e.target.value,
          themeID: item.themeID,
          uniqueID: null
        };
      } else {
        newTheme = {
          themeValue: e.target.value === '' ? '0' : e.target.value,
          themeID: item.themeID,
          uniqueID: null
        };
      }
      tempBookThemesValue.push(newTheme);
    }

    this.setState((state) => ({
      ...state,
      colorInterface: {
        ...state.colorInterface,
        colorInfo: {
          bookThemes: observable.array(tempBookThemesValue)
        }
      }
    }));
  };

  handleSave = async () => {
    const { colorInterface: colorInterfaceFromProps, pxeProductizationState, booksState } = this.props;
    const { colorInterface } = this.state;
    const { pdzProductID } = colorInterface.languageInfo;
    const isPxe = booksState?.bookDetail?.titleType === constants.ETEXT2_PXE_TITLE;

    if (colorInterfaceFromProps.isPdzValidation === true && isPxe && pdzProductID) {
      pxeProductizationState.reset();
      this.setState({ isSavePending: true, pdzValidationError: '' });
      pxeProductizationState.fetch(pdzProductID);
      // PxeProductizationGetService updates the store
      await when(() => pxeProductizationState.isValid !== null);
      if (pxeProductizationState.isValid) {
        this.proceedWithSave(colorInterface);
      } else {
        this.setState({
          isSavePending: false,
          pdzValidationError: pxeProductizationState.validationError || constants.INVALID_PDZ_PRODUCT_ID
        });
      }
      return;
    }

    this.proceedWithSave(colorInterface);
  };

  applyPxeStudyToolsSuppressed = (payload) => {
    const currentPageNavigationPrintSetting = payload.pageNavigationPrintSetting || {};
    const prevNavMenus = Array.isArray(currentPageNavigationPrintSetting.navMenus)
      ? [...currentPageNavigationPrintSetting.navMenus]
      : [];
    const studyIdx = prevNavMenus.findIndex((item) => item.label === constants.PXE_NAV_MENU_LABEL_STUDY_TOOLS);
    const navMenus =
      studyIdx >= 0
        ? prevNavMenus.map((item, i) => (i === studyIdx ? { ...item, isVisible: false } : item))
        : [...prevNavMenus, { label: constants.PXE_NAV_MENU_LABEL_STUDY_TOOLS, isVisible: false }];
    return {
      ...payload,
      pageNavigationPrintSetting: { ...currentPageNavigationPrintSetting, navMenus }
    };
  };

  proceedWithSave = (colorInterface) => {
    const { match, booksState } = this.props;
    const isPxe = booksState?.bookDetail?.titleType === constants.ETEXT2_PXE_TITLE;
    let updatedPayload = JSON.parse(JSON.stringify(toJS(colorInterface)));
    if (isPxe) {
      updatedPayload = this.applyPxeStudyToolsSuppressed(updatedPayload);
    }
    const changedProductPDZ = updatedPayload.languageInfo.pdzProductID;
    updatedPayload.languageInfo.pdzProductID = changedProductPDZ === '' ? null : changedProductPDZ;
    const payload = {
      data: updatedPayload,
      id: match.params.bookid
    };
    const {
      colorInterface: { savePayload }
    } = this.props;
    savePayload(payload);
  };

  getInterfaceValue = (themeID, bookThemes) => {
    let themeValue = '';
    const url = bookThemes.find((item) => item?.themeID === themeID);
    if (url) {
      themeValue = url.themeValue ? url.themeValue : '';
    }
    return themeValue;
  };

  render() {
    const {
      classes,
      language,
      match,
      colorLanguageStatus,
      colorLanguagePostStatus,
      userPermissions,
      booksState,
      colorInterface,
      pxeProductizationState
    } = this.props;
    const {
      colorInterface: stateColorInterface,
      colorInterfaceLabelName,
      pdzValidationError,
      isSavePending
    } = this.state;
    const pxeProductizationIsValid = pxeProductizationState.isValid;
    const pxeProductizationValidationError = pxeProductizationState.validationError;
    const pdzProductFieldMessage =
      pdzValidationError ||
      (isSavePending && pxeProductizationIsValid === false
        ? pxeProductizationValidationError || constants.INVALID_PDZ_PRODUCT_ID
        : '');
    const { colorInfo } = stateColorInterface;
    const { languageInfo } = stateColorInterface;
    const pxeFromStore = colorInterface ? toJS(colorInterface).pageNavigationPrintSetting : undefined;
    const pxeFromState = stateColorInterface.pageNavigationPrintSetting;
    const isPxePlainObject = pxeFromState && typeof pxeFromState === 'object' && !pxeFromState.$mobx;
    const pageNavigationPrintSetting = isPxePlainObject ? pxeFromState : pxeFromStore;
    const { bookThemes } = colorInfo;
    const isPxe = booksState?.bookDetail?.titleType === constants.ETEXT2_PXE_TITLE;
    const pdzProductIdEditable =
      !isPxe || Number(colorInterface?.statusId) === 1 || Number(colorInterface?.statusId) === 10;
    const permissionsList = toJS(userPermissions.permissionList.permissions);

    return (
      <>
        <LoadingHandler
          loading={colorLanguageStatus.isPending || colorLanguagePostStatus.isPending}
          isError={colorLanguageStatus.isError && colorLanguageStatus.error && colorLanguageStatus.error.status === 401}
          loadingContent={<Loader />}
          errorContent={<StateErrorDisplay error={colorLanguageStatus.error} showDetails />}
          content={
            <div className={classes.root}>
              <Breadcrumbs
                data={
                  isPxe ? (
                    <FormattedMessage {...language.getText('interface.PAGE_NAV_PRINT_SETTINGS')} />
                  ) : (
                    <FormattedMessage {...language.getText('desc.INTERFACE_COLORS')} />
                  )
                }
                id={match.params.bookid}
              />
              <br />
              <Paper elevation={3}>
                <br />
                {isPxe ? (
                  <>
                    <Typography variant="h5" color="textPrimary" className={classes.subgridMargin}>
                      <FormattedMessage {...language.getText('interface.PAGE_NAV_PRINT_SETTINGS')} />
                    </Typography>
                    <br />
                    <Typography variant="subtitle1" color="textSecondary" className={classes.subgridMargin}>
                      <FormattedMessage {...language.getText('interface.SELECT_PAGE_NAV_ELEMENTS')} />
                    </Typography>
                    <Grid container spacing={2} className={classes.subgridMargin}>
                      <Grid item xs={5}>
                        <FormControl component="fieldset" className={classes.formControl}>
                          <FormGroup row className={classes.labelName}>
                            <FormControlLabel
                              className={classes.labelName}
                              classes={{ label: classes.switchLabelTypography }}
                              control={
                                <Switch
                                  name="gotoPage"
                                  checked={pageNavigationPrintSetting?.gotoPage === true}
                                  onChange={(e) => this.handleChange(e, false)}
                                  color="primary"
                                />
                              }
                              label={<FormattedMessage {...language.getText('interface.GO_TO_PAGE_FIELD')} />}
                            />
                            <FormControlLabel
                              className={classes.labelName}
                              classes={{ label: classes.switchLabelTypography }}
                              control={
                                <Switch
                                  name="enablePaging"
                                  checked={pageNavigationPrintSetting?.enablePaging === true}
                                  onChange={(e) => this.handleChange(e, false)}
                                  color="primary"
                                />
                              }
                              label={<FormattedMessage {...language.getText('interface.PREVIOUS_NEXT_PAGE_BUTTONS')} />}
                            />
                            {!isPxe && (
                              <FormControlLabel
                                className={classes.labelName}
                                classes={{ label: classes.switchLabelTypography }}
                                control={
                                  <Switch
                                    name={constants.PXE_NAV_MENU_LABEL_STUDY_TOOLS}
                                    color="primary"
                                    checked={
                                      pageNavigationPrintSetting?.navMenus?.find(
                                        (item) => item.label === constants.PXE_NAV_MENU_LABEL_STUDY_TOOLS
                                      )?.isVisible === true
                                    }
                                    onChange={(e) => this.handleChange(e, false)}
                                  />
                                }
                                label={<FormattedMessage {...language.getText('interface.STUDY_TOOLS_NAV')} />}
                              />
                            )}
                            <div className={classes.nextSectionBreak}>
                              <FormControlLabel
                                className={classes.labelName}
                                classes={{ label: classes.switchLabelTypography }}
                                control={
                                  <Switch
                                    name="offlineAccess"
                                    checked={pageNavigationPrintSetting?.offlineAccess === true}
                                    onChange={(e) => this.handleChange(e, false)}
                                    color="primary"
                                  />
                                }
                                label={<FormattedMessage {...language.getText('interface.OFFLINE_ACCESS')} />}
                              />
                              {!pageNavigationPrintSetting?.offlineAccess && (
                                <Typography className={classes.offlineWarning}>
                                  * <FormattedMessage {...language.getText('interface.OFFLINE_ACCESS_WARNING')} />
                                </Typography>
                              )}
                            </div>
                          </FormGroup>
                        </FormControl>
                      </Grid>
                      <Grid item xs={5}>
                        <FormControl component="fieldset" className={classes.formControl}>
                          <FormGroup>
                            <FormControlLabel
                              className={classes.labelName}
                              classes={{ label: classes.switchLabelTypography }}
                              control={
                                <Switch
                                  name="enableIpadSwipe"
                                  checked={pageNavigationPrintSetting?.enableIpadSwipe === true}
                                  onChange={(e) => this.handleChange(e, false)}
                                  color="primary"
                                />
                              }
                              label={<FormattedMessage {...language.getText('interface.IPAD_SWIPE_ACTIONS')} />}
                            />
                            <FormControlLabel
                              className={classes.labelName}
                              classes={{ label: classes.switchLabelTypography }}
                              control={
                                <Switch
                                  name="includeMathMLLib"
                                  checked={pageNavigationPrintSetting?.includeMathMLLib === true}
                                  onChange={(e) => this.handleChange(e, false)}
                                  color="primary"
                                />
                              }
                              label={<FormattedMessage {...language.getText('interface.INCLUDE_MATHML_LIBRARIES')} />}
                            />
                            <FormControlLabel
                              className={classes.labelName}
                              classes={{ label: classes.switchLabelTypography }}
                              control={
                                <Switch
                                  name="preventCopy"
                                  checked={pageNavigationPrintSetting?.preventCopy === true}
                                  onChange={(e) => this.handleChange(e, false)}
                                  color="primary"
                                />
                              }
                              label={<FormattedMessage {...language.getText('interface.PREVENT_COPY')} />}
                            />
                            <FormControlLabel
                              className={classes.labelName}
                              classes={{ label: classes.switchLabelTypography }}
                              control={
                                <Switch
                                  name="enablePrinting"
                                  checked={pageNavigationPrintSetting?.enablePrinting === true}
                                  onChange={(e) => this.handleChange(e, false)}
                                  color="primary"
                                />
                              }
                              label={<FormattedMessage {...language.getText('interface.ENABLE_USER_PRINTING')} />}
                            />
                            {/* PXE print scope (current page / entire book) — UI hidden
                            <FormControl component="fieldset" style={{ marginLeft: 24 }}>
                              <RadioGroup
                                row
                                name="printOption"
                                value={languageInfo.printOption || 'currentPage'}
                                onChange={(e, value) =>
                                  this.handleChange({ target: { name: 'printOption', value } }, true)
                                }
                              >
                                <FormControlLabel
                                  value="currentPage"
                                  control={<Radio color="primary" />}
                                  label={<FormattedMessage {...language.getText('interface.PRINT_CURRENT_PAGE')} />}
                                  disabled={!(pageNavigationPrintSetting?.enablePrinting === true)}
                                  classes={{ label: classes.switchLabelTypography }}
                                />
                                <FormControlLabel
                                  value="entireBook"
                                  control={<Radio color="primary" />}
                                  label={<FormattedMessage {...language.getText('interface.PRINT_ENTIRE_BOOK')} />}
                                  disabled={!(pageNavigationPrintSetting?.enablePrinting === true)}
                                  classes={{ label: classes.switchLabelTypography }}
                                />
                              </RadioGroup>
                            </FormControl>
                            */}
                          </FormGroup>
                        </FormControl>
                      </Grid>
                    </Grid>
                    <br />
                    <Typography variant="subtitle1" color="textSecondary" className={classes.subgridMargin}>
                      <FormattedMessage {...language.getText('interface.AUDIO_BOOK')} />
                    </Typography>
                    <div className={classes.subgridMargin} style={{ marginTop: 8 }}>
                      <div className={classes.segmentControl}>
                        {constants.AUDIO_BOOK_SEGMENT_VALUES.map((option) => (
                          <button
                            key={option}
                            type="button"
                            className={`${classes.segmentButton} ${
                              pageNavigationPrintSetting?.audioBook === option ? 'active' : ''
                            }`}
                            onClick={() => this.handlePxeSegmentChange(option)}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                    <br />
                  </>
                ) : (
                  <>
                    <Typography align="center" variant="h5">
                      <FormattedMessage {...language.getText('interface.SCHEME')} />
                    </Typography>
                    <br />
                    <Typography align="center" variant="subtitle2">
                      <FormattedMessage {...language.getText('interface.TEXT')} />
                    </Typography>
                    <br />

                    {colorInterfaceLabelName.map((item, index) => (
                      <Grid container direction="row" alignItems="center" spacing={1} className={classes.subgridMargin}>
                        <Grid item xs={3}>
                          <Typography variant="h6" color="textPrimary">
                            {item.type}
                          </Typography>
                        </Grid>
                        {item.themeID !== 20 ? (
                          <Grid item xs={2}>
                            {item.themeID !== 14 && item.themeID !== 18 ? (
                              <TextField
                                margin="dense"
                                value={this.getInterfaceValue(item.themeID, bookThemes)}
                                className={classes.customWidth}
                                type="text"
                                InputProps={{
                                  classes: { input: classes.textfieldInput },
                                  inputProps: {
                                    min: 0
                                  }
                                }}
                                variant="outlined"
                                onChange={(e) => this.swatchChange(e, index, item)}
                              />
                            ) : (
                              <TextField
                                margin="dense"
                                value={this.getInterfaceValue(item.themeID, bookThemes)}
                                className={classes.customWidth}
                                type="number"
                                InputProps={{
                                  classes: { input: classes.textfieldInput },
                                  inputProps: {
                                    min: 0,
                                    max: item.themeID === 18 ? 10 : 99
                                  }
                                }}
                                onInput={(e) => {
                                  e.target.value =
                                    item.themeID === 18
                                      ? Math.max(0, parseInt(e.target.value, 10)).toString().slice(0, 1)
                                      : Math.max(0, parseInt(e.target.value, 10)).toString().slice(0, 2);
                                }}
                                variant="outlined"
                                onChange={(e) => this.swatchChange(e, index, item)}
                              />
                            )}
                          </Grid>
                        ) : (
                          <Grid item xs={5}>
                            <FormControl component="fieldset" className={classes.formControl}>
                              <FormGroup row className={classes.labelName}>
                                <FormControlLabel
                                  className={classes.labelName}
                                  control={
                                    <Checkbox
                                      onChange={(e) => this.handleChange(e, false)}
                                      name="isUnderLineHotspot"
                                      color="primary"
                                      checked={languageInfo.isUnderLineHotspot === 'Y'}
                                    />
                                  }
                                />
                              </FormGroup>
                            </FormControl>
                          </Grid>
                        )}
                        {item.themeID !== 18 && item.themeID !== 14 && item.themeID !== 20 && (
                          <Grid item xs={1}>
                            <input
                              type="color"
                              value={this.getInterfaceValue(item.themeID, bookThemes)}
                              onChange={(e) => this.swatchChange(e, index, item)}
                            />
                          </Grid>
                        )}
                      </Grid>
                    ))}
                    <hr />

                    <Grid container direction="row" alignItems="center" spacing={1} className={classes.subgridMargin}>
                      <Grid item xs={6}>
                        <Grid item xs={10}>
                          <Typography variant="h5" color="textPrimary">
                            <FormattedMessage {...language.getText('interface.PLAYER')} />
                          </Typography>
                          <FormControl component="fieldset" className={classes.formControl}>
                            <FormGroup row className={classes.labelName}>
                              <FormControlLabel
                                className={classes.labelName}
                                control={
                                  <Checkbox
                                    onChange={(e) => this.handleChange(e, false)}
                                    name="audioPlayerOnereader"
                                    color="primary"
                                    checked={
                                      languageInfo.audioPlayerOnereader === true ||
                                      languageInfo.audioPlayerOnereader === 'Y'
                                    }
                                  />
                                }
                                label={<FormattedMessage {...language.getText('interface.AUDIO_ONEREADER')} />}
                              />
                            </FormGroup>
                          </FormControl>
                        </Grid>
                        <Grid item xs={10}>
                          <FormControl component="fieldset" className={classes.formControl}>
                            <FormGroup row className={classes.labelName}>
                              <FormControlLabel
                                className={classes.labelName}
                                control={
                                  <Checkbox
                                    onChange={(e) => this.handleChange(e, false)}
                                    name="studyToolOnereader"
                                    color="primary"
                                    checked={languageInfo.studyToolOnereader === 'Y'}
                                  />
                                }
                                label={<FormattedMessage {...language.getText('interface.STUDY_ONEREADER')} />}
                              />
                            </FormGroup>
                          </FormControl>
                        </Grid>
                        <Grid item xs={10}>
                          <FormControl component="fieldset" className={classes.formControl}>
                            <FormGroup row className={classes.labelName}>
                              <FormControlLabel
                                className={classes.labelName}
                                control={
                                  <Checkbox
                                    name="serverSideEnabledOneReader"
                                    color="primary"
                                    checked={
                                      languageInfo.serverSideEnabledOneReader === true ||
                                      languageInfo.serverSideEnabledOneReader === 'Y'
                                    }
                                    onChange={(e) => this.handleChange(e, false)}
                                  />
                                }
                                label={
                                  <FormattedMessage {...language.getText('interface.SERVER_SIDE_ENABLED_ONEREADER')} />
                                }
                              />
                            </FormGroup>
                          </FormControl>
                        </Grid>
                      </Grid>
                      <Grid item xs={4}>
                        <Grid item xs={10}>
                          <Typography variant="h6" color="textPrimary">
                            <FormattedMessage {...language.getText('interface.PRINT_SELECTION')} />
                          </Typography>
                          <FormControl component="fieldset" className={classes.formControl}>
                            <FormGroup row className={classes.labelName}>
                              <FormControlLabel
                                className={classes.labelName}
                                control={
                                  <Checkbox
                                    name="print"
                                    checked={languageInfo.print === constants.YES}
                                    color="primary"
                                    onChange={(e) => this.handleChange(e, false)}
                                  />
                                }
                                label={<FormattedMessage {...language.getText('interface.PRINTING_ENABLED')} />}
                              />
                            </FormGroup>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
                <hr />
                <Grid container direction="row" alignItems="center" spacing={1} className={classes.subgridMargin}>
                  <Grid item xs={2}>
                    <Typography variant="h6" color="textPrimary">
                      <FormattedMessage {...language.getText('interface.PDZ_PRODUCT')} />
                    </Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <Grid container direction="row" alignItems="center" spacing={2} wrap="nowrap">
                      <Grid item>
                        <TextField
                          className={classes.pdzProductTextField}
                          margin="dense"
                          name="pdzProductID"
                          value={languageInfo.pdzProductID}
                          onChange={(e) => this.handleChange(e, true)}
                          variant="outlined"
                          error={!!pdzProductFieldMessage}
                          disabled={!pdzProductIdEditable}
                        />
                      </Grid>
                      {pdzProductFieldMessage ? (
                        <Grid item xs zeroMinWidth>
                          <Typography variant="body2" color="error" component="span">
                            {pdzProductFieldMessage}
                          </Typography>
                        </Grid>
                      ) : null}
                    </Grid>
                  </Grid>
                </Grid>
                <br />
                <br />

                <Grid
                  container
                  spacing={3}
                  direction="row"
                  justify="center"
                  alignItems="center"
                  className={classes.paper}
                >
                  <Grid item>
                    <TooltipCustom
                      title={
                        !permissionsList.includes('authoring_can_edit') ? (
                          <FormattedMessage {...language.getText('user.PERMISSIONS_LABEL')} />
                        ) : (
                          <FormattedMessage {...language.getText('interface.SAVE_INTERFACE_SETTINGS')} />
                        )
                      }
                    >
                      <span>
                        <Button
                          style={{ backgroundColor: '#005d83' }}
                          variant="outlined"
                          color="primary"
                          onClick={this.handleSave}
                          disabled={!permissionsList.includes('authoring_can_edit')}
                        >
                          <FormattedMessage {...language.getText('common.SAVE')} />
                        </Button>
                      </span>
                    </TooltipCustom>
                  </Grid>
                  <Grid item>
                    <TooltipCustom
                      title={<FormattedMessage {...language.getText('interface.CANCEL_INTERFACE_SETTINGS')} />}
                    >
                      <Button
                        style={{ backgroundColor: '#005d83' }}
                        variant="outlined"
                        onClick={() => Framework.redirectTo(`/books/${match.params.bookid}`)}
                        color="primary"
                      >
                        <FormattedMessage {...language.getText('common.CANCEL')} />
                      </Button>
                    </TooltipCustom>
                  </Grid>
                </Grid>
                <br />
                <br />
              </Paper>
            </div>
          }
        />
      </>
    );
  }
}

export default withStyles(styles)(observer(inject('language')(InterfaceColors)));
